import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AccordionSection, H3 } from 'components';
import CalculationBreakdownList from 'components/Calculator/CalculationBreakdownList/CalculationBreakdownList';
import { Tag } from 'components/Tag';
import { Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { Size } from 'design/types';
import { ICalculatorRule, ICategoryGroup, IPOSData, ITipCalculatorResultEmployee } from 'types';
import { centsToDollarsDisplay, toTitleCase } from 'utils';

interface IOtherContributorsBreakdownProps {
  calculations: ITipCalculatorResultEmployee[];
  calculatorRules: ICalculatorRule[];
  categoryGroups: ICategoryGroup[];
  creditCardFee?: number;
  dataCy?: string;
  isVirtual?: boolean;
  posData: IPOSData;
}

const HeaderContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const StyledTag = styled(Tag)`
  background-color: ${theme.color.neutral[200]};
  margin-bottom: ${toRem(theme.space.xs)};
`;

const HeaderLabel = styled(Text)`
  color: ${theme.color.neutral[500]};
  margin-bottom: ${toRem(theme.space.xs)};
`;

const HeaderTotal = styled(Text)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const POSBreakdown = styled(CalculationBreakdownList)`
  background-color: ${theme.color.neutral[50]};
  width: 100%;
  padding: 0 ${toRem(theme.space.lg)} ${toRem(theme.space.lg)} ${toRem(theme.space.lg)};
`;

const OtherContributorsBreakdown = ({ calculations, calculatorRules, categoryGroups, creditCardFee, isVirtual = false, posData, dataCy = 'other-contributors-list-item' }: IOtherContributorsBreakdownProps) => {
  const collectedPOSTotal = useMemo(() => posData.segments?.reduce((acc, segment) => acc + segment.totals.gross, 0) || 0, [posData.segments]);

  return (
    <AccordionSection
      key={`other-contributors-${posData.provider.employeeId}`}
      dataCy={dataCy}
      open={false}
      compact
      title={
        <HeaderContainer data-cy="other-contributors-header">
          {isVirtual && <StyledTag label="Virtual" size={Size.SMALL} />}
          <H3>{toTitleCase(posData.provider.name)}</H3>
        </HeaderContainer>
      }
      actions={
        <HeaderContainer data-cy="other-contributors-collected-total">
          <HeaderLabel size="xxsmall" fontWeight="medium" color={theme.color.neutral[500]}>
            Collected
          </HeaderLabel>
          <HeaderTotal fontWeight="medium">{centsToDollarsDisplay(collectedPOSTotal)}</HeaderTotal>
        </HeaderContainer>
      }
    >
      <POSBreakdown calculations={calculations} calculatorRules={calculatorRules} categoryGroups={categoryGroups} segments={posData.segments} creditCardFee={creditCardFee} contributingOnly />
    </AccordionSection>
  );
};

export default OtherContributorsBreakdown;
